import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  MapPin,
  Users,
  Trophy,
  Clock,
  Search,
  ArrowRight,
  X,
  AlertCircle,
  CalendarClock,
} from 'lucide-react';
import { format } from 'date-fns';
import api from '../../services/api';
import {
  SPORT_OPTIONS,
  sportGradient,
  statusStyle,
  capitalize,
  formatLabel,
  formatFee,
  countdownLabel,
} from '../../utils/tournamentUi';

interface Tournament {
  _id: string;
  title: string;
  sport: string;
  description: string;
  location: string;
  venue?: string;
  date: string;
  time: string;
  registrationDeadline?: string;
  maxParticipants: number;
  participantCount: number;
  availableSpots?: number;
  registrationOpen?: boolean;
  registrationFee: number;
  prizePool?: string;
  organizer?: string;
  bannerImage?: string;
  status: string;
  format?: string;
}

const inputClass =
  'w-full h-11 rounded-xl border border-slate-200 bg-white text-sm text-slate-900 placeholder:text-slate-400 ' +
  'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition';

const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden animate-pulse">
    <div className="h-40 bg-slate-200" />
    <div className="p-5 space-y-3">
      <div className="h-5 w-3/4 bg-slate-200 rounded" />
      <div className="h-3 w-full bg-slate-100 rounded" />
      <div className="h-3 w-5/6 bg-slate-100 rounded" />
      <div className="h-3 w-1/2 bg-slate-100 rounded mt-4" />
      <div className="h-2 w-full bg-slate-100 rounded-full mt-4" />
    </div>
  </div>
);

const TournamentsPage: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sportFilter, setSportFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('upcoming');

  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchTournaments = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await api.get('/tournaments', {
        params: {
          ...(searchTerm ? { search: searchTerm } : {}),
          ...(sportFilter ? { sport: sportFilter } : {}),
          // '' must be sent: the API defaults to upcoming-only when status is absent
          status: statusFilter,
        },
      });
      if (resp.data?.success) {
        setTournaments(resp.data.data || []);
        setError(null);
      } else {
        setError('Failed to fetch tournaments');
      }
    } catch (err) {
      setError('Could not load tournaments. Check your connection and try again.');
      console.error('Error fetching tournaments:', err);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, sportFilter, statusFilter]);

  useEffect(() => {
    fetchTournaments();
  }, [fetchTournaments]);

  const hasFilters = Boolean(searchInput || sportFilter || statusFilter !== 'upcoming');

  const clearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setSportFilter('');
    setStatusFilter('upcoming');
  };

  const safeDate = (value: string, pattern: string) => {
    const d = new Date(value);
    return isNaN(d.getTime()) ? '' : format(d, pattern);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="bg-slate-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_20%,#6366f1_0,transparent_45%),radial-gradient(circle_at_85%_70%,#10b981_0,transparent_40%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <p className="text-xs font-semibold uppercase tracking-widest text-indigo-300 mb-3">
            Sportofolo Events
          </p>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight">Tournaments</h1>
          <p className="mt-3 text-base sm:text-lg text-slate-300 max-w-2xl">
            Find a competition near you, check the details and register in a few taps.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 pb-16 relative">
        {/* Filters */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-5 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_200px_200px] gap-3">
            <div className="relative">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                aria-label="Search tournaments"
                placeholder="Search by name, place or description"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className={`${inputClass} pl-10 pr-4`}
              />
            </div>
            <select
              aria-label="Filter by sport"
              value={sportFilter}
              onChange={(e) => setSportFilter(e.target.value)}
              className={`${inputClass} px-3`}
            >
              <option value="">All sports</option>
              {SPORT_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className={`${inputClass} px-3`}
            >
              <option value="upcoming">Upcoming</option>
              <option value="ongoing">Ongoing</option>
              <option value="completed">Completed</option>
              <option value="">All statuses</option>
            </select>
          </div>
          <div className="flex items-center justify-between mt-3 min-h-[24px]">
            <p className="text-sm text-slate-500" aria-live="polite">
              {loading ? 'Searching…' : `${tournaments.length} tournament${tournaments.length === 1 ? '' : 's'}`}
            </p>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800"
              >
                <X className="w-3.5 h-3.5" /> Clear filters
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && !loading && (
          <div className="bg-white border border-red-200 rounded-2xl p-8 text-center">
            <AlertCircle className="mx-auto h-10 w-10 text-red-400 mb-3" />
            <h2 className="text-lg font-semibold text-slate-900">Something went wrong</h2>
            <p className="text-slate-600 mt-1 mb-4">{error}</p>
            <button
              onClick={fetchTournaments}
              className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-medium hover:bg-indigo-700 transition-colors"
            >
              Try again
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && !error && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        )}

        {/* Empty */}
        {!loading && !error && tournaments.length === 0 && (
          <div className="bg-white border border-dashed border-slate-300 rounded-2xl text-center py-16 px-6">
            <Trophy className="mx-auto h-12 w-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-900">No tournaments found</h3>
            <p className="text-slate-500 mt-1 mb-5">
              {hasFilters
                ? 'Nothing matches those filters. Try widening your search.'
                : 'New tournaments will show up here as soon as they are announced.'}
            </p>
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="bg-indigo-600 text-white px-5 py-2.5 rounded-xl font-medium hover:bg-indigo-700 transition-colors"
              >
                Reset filters
              </button>
            )}
          </div>
        )}

        {/* Grid */}
        {!loading && !error && tournaments.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tournaments.map((t) => {
              const spotsLeft =
                typeof t.availableSpots === 'number'
                  ? t.availableSpots
                  : Math.max(t.maxParticipants - t.participantCount, 0);
              const fillPct = Math.min(
                100,
                Math.round((t.participantCount / Math.max(t.maxParticipants, 1)) * 100)
              );
              const isFull = spotsLeft <= 0;
              const regOpen = t.registrationOpen !== false && !isFull && t.status === 'upcoming';
              const countdown = t.status === 'upcoming' ? countdownLabel(t.date) : null;

              return (
                <Link
                  key={t._id}
                  to={`/tournaments/${t._id}`}
                  className="group bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col
                             hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 transition-all duration-200
                             focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                  {/* Banner */}
                  <div className={`h-40 relative bg-gradient-to-br ${sportGradient(t.sport)}`}>
                    {t.bannerImage ? (
                      <img
                        src={t.bannerImage}
                        alt=""
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Trophy className="absolute right-4 bottom-3 w-20 h-20 text-white/20" />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

                    {/* Date chip */}
                    <div className="absolute top-3 left-3 bg-white rounded-xl shadow-md px-3 py-1.5 text-center leading-none">
                      <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-600">
                        {safeDate(t.date, 'MMM')}
                      </div>
                      <div className="text-xl font-bold text-slate-900 mt-0.5">
                        {safeDate(t.date, 'dd')}
                      </div>
                    </div>

                    <span
                      className={`absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ring-inset ${statusStyle(
                        t.status
                      )}`}
                    >
                      {capitalize(t.status)}
                    </span>

                    <span className="absolute bottom-3 left-3 text-xs font-semibold uppercase tracking-widest text-white/90">
                      {capitalize(t.sport)}
                    </span>
                  </div>

                  {/* Body */}
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="text-lg font-bold tracking-tight text-slate-900 group-hover:text-indigo-700 transition-colors line-clamp-2">
                      {t.title}
                    </h3>
                    <p className="text-sm text-slate-600 mt-1.5 line-clamp-2">{t.description}</p>

                    <div className="mt-4 space-y-2 text-sm text-slate-600">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-400 shrink-0" />
                        <span>
                          {safeDate(t.date, 'EEE, MMM d, yyyy')} · {t.time}
                        </span>
                        {countdown && (
                          <span className="ml-auto text-xs font-semibold text-indigo-600 bg-indigo-50 rounded-full px-2 py-0.5">
                            {countdown}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-400 shrink-0" />
                        <span className="truncate">{t.location}</span>
                      </div>
                      {t.format && (
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-slate-400 shrink-0" />
                          <span>{formatLabel(t.format)}</span>
                        </div>
                      )}
                      {t.registrationDeadline && t.status === 'upcoming' && (
                        <div className="flex items-center gap-2">
                          <CalendarClock className="w-4 h-4 text-slate-400 shrink-0" />
                          <span>Register by {safeDate(t.registrationDeadline, 'MMM d')}</span>
                        </div>
                      )}
                    </div>

                    {t.prizePool && (
                      <div className="mt-4 inline-flex items-center gap-2 self-start rounded-lg bg-amber-50 ring-1 ring-inset ring-amber-200 px-3 py-1.5">
                        <Trophy className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-medium text-amber-800">{t.prizePool}</span>
                      </div>
                    )}

                    {/* Capacity */}
                    <div className="mt-5 mb-5">
                      <div className="flex justify-between text-xs font-medium mb-1.5">
                        <span className="text-slate-500">
                          {t.participantCount}/{t.maxParticipants} registered
                        </span>
                        <span className={isFull ? 'text-red-600' : spotsLeft <= 5 ? 'text-amber-600' : 'text-slate-500'}>
                          {isFull ? 'Full' : `${spotsLeft} spot${spotsLeft === 1 ? '' : 's'} left`}
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
                        <div
                          className={`h-full rounded-full ${isFull ? 'bg-red-500' : 'bg-indigo-500'}`}
                          style={{ width: `${fillPct}%` }}
                        />
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="pt-4 border-t border-slate-100 flex items-center justify-between mt-auto">
                      <div>
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
                          Entry
                        </p>
                        <p className="text-base font-bold text-slate-900">{formatFee(t.registrationFee)}</p>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1.5 text-sm font-semibold ${
                          regOpen ? 'text-indigo-600' : 'text-slate-500'
                        }`}
                      >
                        {regOpen ? 'Register' : 'View details'}
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentsPage;
